import React, { useState, useEffect } from 'react';
import { useWallet } from '@solana/wallet-adapter-react';
import { PollClient } from '../poll-client';
import * as anchor from '@project-serum/anchor';
import { Connection, PublicKey } from '@solana/web3.js';

interface DecryptedThroneProps {
  isOpen: boolean;
  onClose: () => void;
  showResults?: boolean;
}

// Интерфейс для результатов голосования
interface VoteResult {
  option_index: number;
  votes: string;
  percentage: number;
}

interface Question {
  text: string;
  options: string[];
  correctAnswer: number;
}

// Интерфейс для шага анимации
interface AnimationStep {
  heading: string;
  details: string[];
}

const WEEKLY_VOTE_PROGRAM_ID = new PublicKey("6TxniqSYQXCZjghxftkJH3znM7A7m1HBpwsQdh7vQogD");

// Определяем шаги анимации после голосования
const animationSteps: AnimationStep[] = [
  {
    heading: "Your vote was submitted.",
    details: [
      "✅ Vote encrypted locally on user device. No one, not even Arcium, can see the content."
    ]
  },
  {
    heading: "It was instantly encrypted so no one could see what it was.",
    details: [
      "✅ User-side encryption complete. Routing via MXE…"
    ]
  },
  {
    heading: "The encrypted vote entered the Arcium system.",
    details: [
      "✅ Routing through ARXOS… initialized."
    ]
  },
  {
    heading: "It was sent to the voting chamber, the \"Throne Room.\"",
    details: [
      "✅ Destination: Throne Room Cluster."
    ]
  },
  {
    heading: "There, it was split among several secure computers, so none of them could see your vote on their own.",
    details: [
      "✅ ARX Node cluster received the vote.",
      "✅ Splitting vote into secure shares… complete."
    ]
  },
  {
    heading: "The computers worked together to count the votes, without ever decrypting them.",
    details: [
      "✅ Launching MPC session.",
      "✅ Computing result with other encrypted shares… processing."
    ]
  },
  {
    heading: "Only the final result was produced, without leaking any individual vote.",
    details: [
      "✅ No party sees individual votes. Only output is revealed."
    ]
  },
  {
    heading: "The result came back to us, and it's about to reveal who advances to the next round.",
    details: [
      "✅ Applying SHE: Homomorphic result finalized.",
      "✅ Final tally ready.",
      "✅ Output dispatched to Decrypted Throne panel."
    ]
  }
];

// Добавляем стили для анимации глитча
const glitchStyles = `
@keyframes fadeIn {
  0% {
    opacity: 0;
    transform: translateY(10px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
}

.fade-in {
  opacity: 0;
  animation: fadeIn 0.6s ease-out forwards;
}

.delay-100 {
  animation-delay: 100ms;
}

.delay-300 {
  animation-delay: 300ms;
}

.delay-500 {
  animation-delay: 500ms;
}

.delay-700 {
  animation-delay: 700ms;
}

.delay-900 {
  animation-delay: 900ms;
}

@keyframes glitchEffect {
  0% {
    opacity: 0;
    clip-path: polygon(0 0, 100% 0, 100% 0, 0 0);
    transform: translateX(-5px);
  }
  10% {
    clip-path: polygon(0 0, 100% 0, 100% 25%, 0 25%);
    transform: translateX(5px);
  }
  15% {
    clip-path: polygon(0 10%, 100% 10%, 100% 15%, 0 15%);
    transform: translateX(-5px);
  }
  20% {
    clip-path: polygon(0 15%, 100% 15%, 100% 30%, 0 30%);
    transform: translateX(-3px);
  }
  25% {
    clip-path: polygon(0 20%, 100% 20%, 100% 25%, 0 25%);
    transform: translateX(4px);
  }
  30% {
    clip-path: polygon(0 25%, 100% 25%, 100% 40%, 0 40%);
    transform: translateX(3px);
  }
  35% {
    clip-path: polygon(0 30%, 100% 30%, 100% 35%, 0 35%);
    transform: translateX(-4px);
  }
  40% {
    clip-path: polygon(0 40%, 100% 40%, 100% 55%, 0 55%);
    transform: translateX(2px);
  }
  45% {
    clip-path: polygon(0 45%, 100% 45%, 100% 50%, 0 50%);
    transform: translateX(-2px);
  }
  50% {
    clip-path: polygon(0 55%, 100% 55%, 100% 70%, 0 70%);
    transform: translateX(-2px);
  }
  55% {
    clip-path: polygon(0 60%, 100% 60%, 100% 65%, 0 65%);
    transform: translateX(3px);
  }
  60% {
    clip-path: polygon(0 70%, 100% 70%, 100% 85%, 0 85%);
    transform: translateX(1px);
  }
  65% {
    clip-path: polygon(0 75%, 100% 75%, 100% 80%, 0 80%);
    transform: translateX(-3px);
  }
  70% {
    clip-path: polygon(0 85%, 100% 85%, 100% 100%, 0 100%);
    transform: translateX(-1px);
  }
  80%, 100% {
    clip-path: polygon(0 0, 100% 0, 100% 100%, 0 100%);
    transform: translateX(0);
    opacity: 1;
  }
}

@keyframes textGlitch {
  0% {
    opacity: 1;
    transform: translateX(0) skewX(0);
    text-shadow: none;
  }
  5% {
    opacity: 0.8;
    transform: translateX(-3px) skewX(4deg);
    text-shadow: -2px 0 #8b5cf6, 1px 0 #22c55e;
  }
  10% {
    opacity: 0.9;
    transform: translateX(-2px) skewX(3deg);
    text-shadow: -2px 0 #8b5cf6, 1px 0 #06b6d4;
  }
  15% {
    opacity: 1;
    transform: translateX(3px) skewX(-4deg);
    text-shadow: 2px 0 #d946ef, -1px 0 #06b6d4;
  }
  20% {
    transform: translateX(2px) skewX(-3deg);
    text-shadow: 2px 0 #d946ef, -1px 0 #22c55e;
  }
  25% {
    opacity: 0.9;
    transform: translateX(-2px) skewX(2deg);
    text-shadow: -2px 0 #8b5cf6, 1px 0 #06b6d4;
  }
  30% {
    opacity: 0.95;
    transform: translateX(-1px) skewX(2deg);
    text-shadow: -1px 0 #8b5cf6;
  }
  35% {
    transform: translateX(2px) skewX(-3deg);
    text-shadow: 1px 0 #d946ef, -1px 0 #06b6d4;
  }
  40% {
    transform: translateX(1px) skewX(-2deg);
    text-shadow: 1px 0 #d946ef;
  }
  45% {
    opacity: 0.9;
    transform: translateX(-2px) skewX(3deg);
    text-shadow: -2px 0 #8b5cf6, 1px 0 #22c55e;
  }
  50% {
    opacity: 1;
    transform: translateX(0) skewX(0);
    text-shadow: none;
  }
  100% {
    opacity: 1;
    transform: translateX(0) skewX(0);
    text-shadow: none;
  }
}

.glitch-text {
  animation: textGlitch 0.5s ease-out forwards;
  display: inline-block;
  position: relative;
}

.glitch-text::before,
.glitch-text::after {
  content: attr(data-text);
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.2);
}

.glitch-text::before {
  left: -2px;
  text-shadow: 2px 0 #ff00ff;
  clip-path: polygon(0 0, 100% 0, 100% 45%, 0 45%);
  animation: glitch-line 0.4s ease-out forwards;
}

.glitch-text::after {
  left: 2px;
  text-shadow: -2px 0 #00ffff;
  clip-path: polygon(0 55%, 100% 55%, 100% 100%, 0 100%);
  animation: glitch-line 0.4s ease-out forwards;
}

@keyframes glitch-line {
  0% {
    transform: translateX(0);
  }
  10% {
    transform: translateX(-2px);
  }
  20% {
    transform: translateX(2px);
  }
  30% {
    transform: translateX(-1px);
  }
  40% {
    transform: translateX(1px);
  }
  50%, 100% {
    transform: translateX(0);
  }
}

.glitch-container {
  position: relative;
  overflow: hidden;
  animation: container-glitch 0.5s ease-out forwards;
}

.glitch-container::before,
.glitch-container::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: 0.8;
  z-index: 1;
}

.glitch-container::before {
  background: rgba(255, 0, 255, 0.2);
  left: -2px;
  clip-path: polygon(0 15%, 100% 15%, 100% 30%, 0 30%);
  animation: container-glitch-line 0.4s ease-out forwards;
}

.glitch-container::after {
  background: rgba(0, 255, 255, 0.2);
  left: 2px;
  clip-path: polygon(0 65%, 100% 65%, 100% 80%, 0 80%);
  animation: container-glitch-line 0.4s ease-out forwards;
}

@keyframes container-glitch {
  0% {
    transform: translateX(0) skewX(0);
    filter: none;
  }
  10% {
    transform: translateX(-2px) skewX(1deg);
    filter: hue-rotate(90deg) contrast(150%);
  }
  20% {
    transform: translateX(2px) skewX(-1deg);
    filter: hue-rotate(-90deg) contrast(150%) brightness(120%);
  }
  30% {
    transform: translateX(-1px) skewX(0.5deg);
    filter: invert(50%);
  }
  40% {
    transform: translateX(1px) skewX(-0.5deg);
    filter: saturate(200%) contrast(120%);
  }
  50%, 85% {
    transform: translateX(0) skewX(0);
    filter: none;
  }
  90% {
    transform: translateX(0) skewX(0);
    filter: brightness(105%);
  }
  95% {
    transform: translateX(0) skewX(0);
    filter: brightness(102%);
  }
  100% {
    transform: translateX(0) skewX(0);
    filter: none;
  }
}

@keyframes container-glitch-line {
  0% {
    transform: translateX(0);
    opacity: 0.5;
  }
  10% {
    transform: translateX(-4px);
    opacity: 0.8;
  }
  20% {
    transform: translateX(4px);
    opacity: 0.6;
  }
  30% {
    transform: translateX(-2px);
    opacity: 0.7;
  }
  40% {
    transform: translateX(2px);
    opacity: 0.8;
  }
  50%, 100% {
    transform: translateX(0);
    opacity: 0;
  }
}

.horizontal-glitch-line {
  position: absolute;
  left: 0;
  height: 1px;
  background-color: rgba(139, 92, 246, 0.7);
  width: 100%;
  z-index: 5;
  animation: horizontal-line-anim 0.4s ease-out forwards;
}

@keyframes horizontal-line-anim {
  0% {
    transform: scaleX(0);
  }
  100% {
    transform: scaleX(1);
  }
}
`;

// Функция для генерации случайных букв и символов для глитч-эффекта
const generateRandomGlitchChar = () => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*()-=_+[]{}|;:,.<>/?';
  return chars.charAt(Math.floor(Math.random() * chars.length));
};

// Функция для создания случайной строки с глитч-символами
const createGlitchText = (text: string) => {
  let result = '';
  for (let i = 0; i < text.length; i++) {
    // С вероятностью 30% заменяем символ на случайный
    if (Math.random() < 0.3) {
      result += generateRandomGlitchChar();
    } else {
      result += text.charAt(i);
    }
  }
  return result;
};

export const DecryptedThrone: React.FC<DecryptedThroneProps> = ({ isOpen, onClose, showResults = false }) => {
  // Сохраняем оригинальную функцию onClose для использования внутри
  const originalOnClose = onClose;
  
  // Переопределяем функцию onClose для гарантированной очистки состояния
  const handleClose = () => {
    // Сбрасываем важные состояния
    setVotingOption(null);
    setIsSubmitting(false);
    // Вызываем оригинальную функцию закрытия
    originalOnClose();
  };
  const [step, setStep] = useState(0); // 0: discord name, 1: quiz, 2: voting
  const [discordName, setDiscordName] = useState('');
  const [answers, setAnswers] = useState<Array<number | null>>([null, null, null, null, null]);
  const [showQuizResults, setShowQuizResults] = useState(false);
  const [votingOption, setVotingOption] = useState<number | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [discordError, setDiscordError] = useState<string | null>(null);
  const [hasCompleted, setHasCompleted] = useState(false);
  const [arciumTxId, setArciumTxId] = useState<string | null>(null);
  const [arciumStatus, setArciumStatus] = useState<string | null>(null);
  const [countdown, setCountdown] = useState(55);
  const [checkingDiscord, setCheckingDiscord] = useState(false);
  
  // Состояния для анимации после голосования
  const [currentAnimationStep, setCurrentAnimationStep] = useState(-1);
  const [animationText, setAnimationText] = useState("");
  const [glitchedText, setGlitchedText] = useState(""); // Текст с глитчем
  const [isTyping, setIsTyping] = useState(false);
  const [animationComplete, setAnimationComplete] = useState(false);
  const [lotteryTicket, setLotteryTicket] = useState<string | null>(null);
  const [glitchLines, setGlitchLines] = useState<{position: number; color: string}[]>([]);

  // Состояния для анимации открытия/закрытия модального окна
  const [isAnimating, setIsAnimating] = useState<boolean>(false);
  const [shouldRender, setShouldRender] = useState<boolean>(false);

  // Добавляем состояние для отображения анимации загрузки
  const [isLoading, setIsLoading] = useState(false);
  const [results, setResults] = useState<VoteResult[]>([]);
  const [totalVotes, setTotalVotes] = useState(0);
  const [winningOption, setWinningOption] = useState<number | null>(null);
  
  const { publicKey, signTransaction } = useWallet();
  const wallet = publicKey?.toString();
  
  // Функция для генерации случайного лотерейного билета
  const generateLotteryTicket = (): string => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'; // Исключаем похожие символы I, O, 0, 1
    let ticket = '';
    // Генерируем 12 символьный код билета
    for (let i = 0; i < 12; i++) {
      ticket += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    // Форматируем в виде XXX-XXX-XXX-XXX
    return `${ticket.slice(0, 3)}-${ticket.slice(3, 6)}-${ticket.slice(6, 9)}-${ticket.slice(9, 12)}`;
  };
  
  // Обработчик закрытия модального окна
  const handleModalClose = () => {
    // Проверяем, можно ли закрыть окно
    if (countdown > 0) {
      // Если таймер еще активен, ничего не делаем
      return;
    }
    
         // Если таймер истек, закрываем окно и сбрасываем состояния
     handleClose();
  };

  // Блокировка прокрутки страницы при открытом модальном окне
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    // Очистка при размонтировании компонента
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && wallet) {
      // Показываем анимацию загрузки при открытии модального окна
      setIsLoading(true);
      
      // Сброс состояний
      setStep(0);
      setDiscordName('');
      setAnswers([null, null, null, null, null]);
      setVotingOption(null); // Гарантированно сбрасываем выбор при открытии
      setSubmitted(false); // Важно: сбрасываем флаг submitted при каждом открытии
      setShowQuizResults(false);
      setError(null);
      setDiscordError(null); // Сбрасываем ошибку проверки Discord имени
      setArciumTxId(null);
      setArciumStatus(null);
      setCurrentAnimationStep(-1); // Сбрасываем шаг анимации
      setAnimationComplete(false); // Сбрасываем состояние завершения анимации
      
      // Если нужно отобразить результаты голосования
      if (showResults) {
        fetchVoteResults();
      }
      
      // Проверяем статус пользователя
      checkCompletion(wallet);
    } else if (isOpen) {
      // Если кошелек не подключен
      setIsLoading(false);
      setHasCompleted(false);
      setStep(0);
      setDiscordName('');
      setAnswers([null, null, null, null, null]);
      setVotingOption(null); // Гарантированно сбрасываем выбор при открытии
      setSubmitted(false); // Важно: сбрасываем флаг submitted при каждом открытии
      setShowQuizResults(false);
      setError(null);
      setArciumTxId(null);
      setArciumStatus(null);
      setCountdown(55);
      setCurrentAnimationStep(-1); // Сбрасываем шаг анимации
      setAnimationComplete(false); // Сбрасываем состояние завершения анимации
      
      // Если нужно отобразить результаты голосования
      if (showResults) {
        fetchVoteResults();
      }
    }
  }, [isOpen, wallet, showResults]);
  
  useEffect(() => {
    let timer: NodeJS.Timeout;
    
    if (submitted && countdown > 0) {
      timer = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
    }
    
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [submitted, countdown]);

  // Управление анимациями открытия/закрытия
  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
      // Small delay to ensure DOM is ready before animation
      const timer = setTimeout(() => setIsAnimating(true), 10);
      return () => clearTimeout(timer);
    } else {
      setIsAnimating(false);
      // Wait for animation to complete before unmounting
      const timer = setTimeout(() => setShouldRender(false), 150);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Функция для получения результатов голосования
  const fetchVoteResults = async () => {
    try {
      const response = await fetch(`/api/decrypted-throne-results`);
      if (!response.ok) {
        const text = await response.text();
        throw new Error('Failed to fetch results');
      }
      
      const data = await response.json();
      
      // Создаем результаты вручную, если формат не соответствует ожидаемому
      let processedResults: VoteResult[] = [];
      let total = 0;
      
      if (data && Array.isArray(data)) {
        // Если сервер вернул просто массив результатов
        processedResults = data.map(item => {
          // Получаем vote_option из данных сервера
          const voteOption = parseInt(item.option_index || item.vote_option || 0);
          return {
            option_index: voteOption,
            votes: item.votes || item.count || "0",
            percentage: item.percentage || 0
          };
        });
        total = processedResults.reduce((sum, item) => sum + parseInt(item.votes), 0);
      } else if (data && data.results && Array.isArray(data.results)) {
        // Если сервер вернул объект с полем results
        processedResults = data.results;
        total = data.totalVotes || processedResults.reduce((sum, item) => sum + parseInt(item.votes), 0);
      } else {
        // Если формат не определен, создаем пустые результаты
        processedResults = [0, 1, 2].map(index => ({
          option_index: index,
          votes: "0",
          percentage: 0
        }));
        total = 0;
      }
      
      // Убедимся, что у нас есть все три опции
      const allOptions = [0, 1, 2];
      const finalResults = allOptions.map(index => {
        const existing = processedResults.find(r => r.option_index === index);
        if (existing) return existing;
        return { option_index: index, votes: "0", percentage: 0 };
      });
      
      // Устанавливаем голоса из базы данных
      setResults(finalResults);
      setTotalVotes(total);
      
      // Определяем победителя, если есть голоса
      if (finalResults.length > 0 && total > 0) {
        const winner = finalResults.reduce((prev, current) => {
          return parseInt(current.votes) > parseInt(prev.votes) ? current : prev;
        });
        setWinningOption(winner.option_index);
      } else {
        // Если нет голосов, то нет победителя
        setWinningOption(null);
      }
    } catch (err) {
      console.error('Error fetching vote results:', err);
      setError('Error fetching vote results');
    }
  };
  
  // Обновляем checkCompletion чтобы получать данные о транзакции и лотерейном билете
  const checkCompletion = async (wallet: string) => {
    try {
      const response = await fetch(`/api/decrypted-throne/${wallet}`);
      
      if (response.status === 404) {
        setHasCompleted(false);
        // Сбрасываем счетчик до 55 для новых пользователей
        setCountdown(55);
        // Важно: гарантированно сбрасываем выбор голосования
        setVotingOption(null);
        setIsLoading(false); // Скрываем анимацию загрузки
        return;
      }
      
      if (response.ok) {
        const data = await response.json();
        setHasCompleted(true);
        
        // При повторном открытии кнопка Close должна быть активна сразу
        setCountdown(0);
        
        // При повторном открытии устанавливаем лотерейный билет из базы данных
        if (data.lottery_ticket) {
          setLotteryTicket(data.lottery_ticket);
        }
        
        // При повторном открытии устанавливаем ID транзакции из базы данных
        if (data.arcium_tx_id) {
          setArciumTxId(data.arcium_tx_id);
        }
        
        setIsLoading(false); // Скрываем анимацию загрузки
      } else {
        setHasCompleted(false);
        // Сбрасываем счетчик до 55 для новых пользователей
        setCountdown(55);
        // Важно: гарантированно сбрасываем выбор голосования
        setVotingOption(null);
        setIsLoading(false); // Скрываем анимацию загрузки
      }
    } catch (error) {
      setHasCompleted(false);
      // Сбрасываем счетчик до 40 для новых пользователей
      setCountdown(40);
      // Важно: гарантированно сбрасываем выбор голосования
      setVotingOption(null);
      setIsLoading(false); // Скрываем анимацию загрузки
    }
  };
  
  const handleStartQuiz = async () => {
    // Проверяем, что имя Discord введено или выбран анонимный режим
    if (!discordName.trim() && discordName !== 'anonymous') return;
    
    // Если выбран анонимный режим, сразу переходим к следующему шагу
    if (discordName === 'anonymous') {
      setStep(1);
      return;
    }
    
    setCheckingDiscord(true);
    setDiscordError(null);
    
    try {
      // Проверим наличие имени непосредственно через запрос к базе данных
      const response = await fetch(`/api/decrypted-throne`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          discord_name: discordName.trim(),
          check_only: true // Специальный флаг для проверки существования
        })
      });
      
      const data = await response.json();
      
      if (response.status === 409 || (data && data.exists)) {
        // Имя уже существует в базе (код 409 Conflict)
        setDiscordError('This Discord username has already been used');
      } else if (response.ok) {
        // Имя уникально, переходим к следующему шагу
        setStep(1);
      } else {
        // Другая ошибка
        throw new Error(data.message || 'Failed to check Discord name');
      }
    } catch (err) {
      setDiscordError('Error checking Discord name. Please try again.');
    } finally {
      setCheckingDiscord(false);
    }
  };
  
  const handleAnswerSelect = (questionIndex: number, answerIndex: number) => {
    if (showQuizResults) return; // Prevent changing answers after results are shown
    
    const newAnswers = [...answers];
    newAnswers[questionIndex] = answerIndex;
    setAnswers(newAnswers);
  };
  
  const allQuestionsAnswered = () => {
    return answers.every(answer => answer !== null);
  };
  
  const handleQuizSubmit = () => {
    if (showQuizResults) {
      setStep(2); // Go to voting step
    } else {
      setShowQuizResults(true); // Show quiz results in the same view
    }
  };
  
  // Функция выбора опции голосования
  const handleVotingOptionSelect = (option: number) => {
    // Принудительное обновление состояния через промис
    return new Promise<void>((resolve) => {
      // Очищаем предыдущее состояние
      setVotingOption(null);
      
      // Устанавливаем небольшую задержку для гарантированного обновления DOM
      setTimeout(() => {
        setVotingOption(option);
        resolve();
      }, 50);
    });
  };
  
  // Функция для анимации текста по буквам без глитч-эффекта во время печати
  const typeText = (text: string) => {
    return new Promise<void>((resolve) => {
      // Сначала устанавливаем пустую строку и ждем немного для корректной инициализации
      setIsTyping(true);
      setAnimationText("");
      setGlitchedText(""); // Очищаем глитч-текст
      setGlitchLines([]); // Очищаем линии глитча
      
      // Сохраняем полный текст отдельно
      const fullText = text;
      
      // Задержка перед началом анимации - уменьшена для экономии времени
      setTimeout(() => {
        let i = 0;
        
        // Гарантируем скорость печати - задаем фиксированное время ~0.8-1.0 секунды на весь текст
        const targetDuration = 800; // 800 мс на всю анимацию печати
        // Если текст длинный, печатаем быстрее; если короткий - медленнее
        const typingSpeed = Math.max(15, Math.min(40, targetDuration / text.length));
        
        // Начинаем с отображения первого символа
        setAnimationText(text.charAt(0));
        i = 1; // Переходим сразу ко второму символу
        
        const typing = setInterval(() => {
          if (i < text.length) {
            // Добавляем следующий символ
            const currentText = text.substring(0, i+1);
            // Устанавливаем текущий видимый текст
            setAnimationText(currentText);
            i++;
          } else {
            clearInterval(typing);
            setIsTyping(false);
            setGlitchedText(""); // Гарантированная очистка глитч-текста
            // Ensure the full text is set clearly
            setAnimationText(fullText);
            resolve();
          }
        }, typingSpeed);
      }, 200); // Уменьшенная задержка для экономии времени
    });
  };
  
  const [prevAnimationStep, setPrevAnimationStep] = useState(-1);
  const [slideDirection, setSlideDirection] = useState('left');

  // Функция для автоматической анимации всех шагов
  useEffect(() => {
    if (!submitted || animationComplete) return;
    
          // Функция для запуска одного шага анимации с расширенным глитч-эффектом
    const animateStep = async (stepIndex: number) => {
      if (stepIndex >= animationSteps.length) {
        // Создаем финальный мощный глитч-эффект для перехода к экрану результатов
        const createFinalGlitchEffect = async () => {
          // Создаем много линий по всему контейнеру
          const generateManyLines = () => {
            const lines = [];
            const lineCount = Math.floor(Math.random() * 15) + 10; // 10-24 линий для максимального эффекта
            
            for (let i = 0; i < lineCount; i++) {
              const position = Math.random() * 100;
              const colors = [
                'rgba(139, 92, 246, 0.9)', 
                'rgba(217, 70, 239, 0.9)', 
                'rgba(6, 182, 212, 0.9)', 
                'rgba(34, 197, 94, 0.9)', 
                'rgba(255, 0, 255, 0.8)', 
                'rgba(0, 255, 255, 0.8)',
                'rgba(255, 255, 0, 0.8)',
                'rgba(255, 0, 0, 0.9)'
              ];
              const color = colors[Math.floor(Math.random() * colors.length)];
              lines.push({ position, color });
            }
            return lines;
          };
          
          // Серия интенсивных глитчей
          for (let i = 0; i < 5; i++) {
            setGlitchLines(generateManyLines());
            setGlitchedText(createGlitchText("Your vote has been recorded!"));
            await new Promise(resolve => setTimeout(resolve, 70));
          }
          
          // Финальный глитч перед переходом
          setGlitchLines(generateManyLines());
          await new Promise(resolve => setTimeout(resolve, 300));
          
          // Очистка глитча и небольшая пауза перед появлением элементов
          setGlitchedText("");
          setGlitchLines([]);
          
          // Небольшая пауза между глитчем и началом появления элементов
          await new Promise(resolve => setTimeout(resolve, 100));
        };
        
        // Выполняем финальный глитч
        await createFinalGlitchEffect();
        
        // Если прошли все шаги
        setAnimationComplete(true);
        
        // Генерируем лотерейный билет
        if (!lotteryTicket) {
          const ticket = generateLotteryTicket();
          setLotteryTicket(ticket);
          
          // Сохраняем билет в базе данных
          fetch('/api/decrypted-throne/update-ticket', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              wallet_address: wallet,
              lottery_ticket: ticket
            })
          }).catch(err => {
            console.error('Failed to update lottery ticket:', err);
          });
        }
        return;
      }
      
      // Сохраняем предыдущий шаг для анимации
      setPrevAnimationStep(currentAnimationStep);
      
      // Применяем эффект глитча вместо свайпа
      setSlideDirection('none');
      
      // Создаем сильный глитч-эффект при смене шага на всё окно с текстом
      const createMajorGlitch = async () => {
        // Создаем несколько случайных линий по всему контейнеру
        const generateLines = () => {
          const lines = [];
          const lineCount = Math.floor(Math.random() * 8) + 5; // 5-12 линий для более заметного эффекта
          
          for (let i = 0; i < lineCount; i++) {
            const position = Math.random() * 100;
            const colors = [
              'rgba(139, 92, 246, 0.8)', 
              'rgba(217, 70, 239, 0.8)', 
              'rgba(6, 182, 212, 0.8)', 
              'rgba(34, 197, 94, 0.8)', 
              'rgba(255, 0, 255, 0.7)', 
              'rgba(0, 255, 255, 0.7)',
              'rgba(255, 255, 0, 0.7)',
              'rgba(255, 0, 0, 0.8)'
            ];
            const color = colors[Math.floor(Math.random() * colors.length)];
            lines.push({ position, color });
          }
          return lines;
        };
        
        // Создаем сильно искаженный текст
        if (stepIndex > 0) {
          const nextText = animationSteps[stepIndex].heading;
          const prevText = animationSteps[stepIndex - 1].heading;
          
                     // Сначала показываем много линий
           setGlitchLines(generateLines());
           
           // Минимальная пауза для эффекта линий
           await new Promise(resolve => setTimeout(resolve, 30));
           
           // Смешиваем предыдущий и следующий текст с случайными символами
           let mixedText = '';
           const maxLen = Math.max(nextText.length, prevText.length);
           
           for (let i = 0; i < maxLen; i++) {
             if (Math.random() < 0.7) { // 70% шанс на случайный символ
               mixedText += generateRandomGlitchChar();
             } else if (i < prevText.length && Math.random() < 0.5) {
               mixedText += prevText.charAt(i);
             } else if (i < nextText.length) {
               mixedText += nextText.charAt(i);
             } else {
               mixedText += generateRandomGlitchChar();
             }
           }
           
           setGlitchedText(mixedText);
           
           // Серия интенсивных глитчей для эффекта перехода между шагами
           // Уменьшено до 3 итераций для соблюдения временного бюджета
           for (let i = 0; i < 3; i++) {
             await new Promise(resolve => setTimeout(resolve, 50));
             
             // Обновляем глитч-текст с разной интенсивностью
             const intensity = Math.min(0.7 + (i * 0.08), 0.9); // Быстрее увеличиваем интенсивность
             let newGlitchText = '';
             
             for (let j = 0; j < mixedText.length; j++) {
               if (Math.random() < intensity) {
                 newGlitchText += generateRandomGlitchChar();
               } else {
                 newGlitchText += mixedText.charAt(j);
               }
             }
             
             setGlitchedText(newGlitchText);
             
             // Обновляем линии
             setGlitchLines(generateLines());
           }
        } else {
                   // Даже для первого шага показываем небольшой глитч
           setGlitchLines(generateLines());
           await new Promise(resolve => setTimeout(resolve, 50));
         }
         
         // Заключительный сильный глитч перед очисткой
         setGlitchLines(generateLines());
         if (stepIndex > 0) {
           setGlitchedText(createGlitchText(animationSteps[stepIndex].heading));
         }
         
         await new Promise(resolve => setTimeout(resolve, 100));
         
         // Полностью сбрасываем глитч для читаемого текста после перехода
        setGlitchedText("");
        setGlitchLines([]);
        setAnimationText(animationSteps[stepIndex].heading); // Устанавливаем чистый текст для читаемости
      };
      
      // Запускаем глитч перед сменой шага
      await createMajorGlitch();
      
      // Устанавливаем текущий шаг
      setCurrentAnimationStep(stepIndex);
      
      // Минимальная пауза для восприятия глитч-эффекта
      await new Promise(resolve => setTimeout(resolve, 50));
      
      // Устанавливаем финальную версию текста без глитча
      setAnimationText(animationSteps[stepIndex].heading);
      setGlitchedText("");
      
      // Печатаем текст шага (быстрее, чтобы уложиться в 4 секунды)
      await typeText(animationSteps[stepIndex].heading);
      
      // Контролируем время каждого шага (общее время не более 5 секунд на шаг)
      // Это обеспечит примерно 40 секунд на все 8 шагов
      
      // Определяем оставшееся время для паузы
      // Вычитаем время, потраченное на глитч (~0.5с) и на печать текста (~1с)
      const totalStepTime = 5000; // 5 секунд на шаг
      const glitchTime = 500; // Примерное время глитча
      const typingTime = Math.min(animationSteps[stepIndex].heading.length * 20, 1200); // Примерное время печати
      const waitTime = Math.max(totalStepTime - glitchTime - typingTime, 1000); // Минимум 1 секунда паузы
      
      await new Promise(resolve => setTimeout(resolve, waitTime));
      
      // Переходим к следующему шагу
      animateStep(stepIndex + 1);
    };
    
    // Запускаем анимацию с первого шага после небольшой задержки
    if (currentAnimationStep === -1) {
      const timer = setTimeout(() => {
        animateStep(0);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [submitted, animationComplete, currentAnimationStep, lotteryTicket, wallet, animationSteps]);
  
  // Сбрасываем анимацию при повторном открытии
  useEffect(() => {
    if (submitted) {
      setCurrentAnimationStep(-1);
      setAnimationComplete(false);
    }
  }, [submitted]);
  

  
  // Обновляем handleVoteSubmit, чтобы четко показывать транзакцию
  // Функция для получения процента голосов за опцию
  const getPercentage = (optionIndex: number): number => {
    if (totalVotes === 0) return 0;
    const optionResult = results.find(r => r.option_index === optionIndex);
    if (!optionResult) return 0;
    return Math.round((parseInt(optionResult.votes) / totalVotes) * 100);
  };
  
  // Функция для получения количества голосов за опцию
  const getVoteCount = (optionIndex: number): number => {
    const optionResult = results.find(r => r.option_index === optionIndex);
    return optionResult ? parseInt(optionResult.votes) : 0;
  };
  
  const handleVoteSubmit = async () => {
    if (votingOption === null || !wallet || (!discordName && discordName !== 'anonymous') || !publicKey || !signTransaction) {
      setError('Please select a voting option and connect your wallet');
      return;
    }
    
    // Проверяем, закрыто ли голосование
    if (showResults) {
      setError('Voting is closed');
      return;
    }
    
    setError(null);
    setIsSubmitting(true);
    setArciumStatus("Preparing transaction...");
    
    // Убираем установку состояний до подтверждения транзакции
    // setSubmitted(true);
    // setStep(2);
    // setCountdown(30);
    
    try {
      // Prepare blockchain transaction
      const connection = new Connection('https://api.devnet.solana.com', 'confirmed');
      
      const provider = new anchor.AnchorProvider(
        connection,
        {
          publicKey,
          signTransaction,
          signAllTransactions: signTransaction
        },
        { commitment: 'confirmed' }
      );
      
      const pollClient = new PollClient(connection, provider.wallet, WEEKLY_VOTE_PROGRAM_ID);

      setArciumStatus("Creating transaction...");

      try {
        // Submit blockchain transaction
        await pollClient.initializeVoteSystem();
        
        const pollId = await pollClient.findAvailablePollId();
        
        const messages = [
          "Sending vote to blockchain...",
          "Waiting for transaction confirmation...",
          "Processing vote..."
        ];
        
        setArciumStatus(messages[0]);
        
        const signature = await pollClient.createNewPoll(pollId, "Decrypted Throne Vote");
        
        setArciumStatus(messages[1]);
        
        const txSignature = pollClient.getLastTransactionSignature() || signature;
        
        // Важно: устанавливаем ID транзакции в состояние
        setArciumTxId(txSignature);
        
        setArciumStatus(messages[2]);
        
        await new Promise(resolve => setTimeout(resolve, 1500));
        
        setArciumStatus("Vote registered successfully!");
        
        // Генерируем лотерейный билет
        const ticket = generateLotteryTicket();
        setLotteryTicket(ticket);
        
        // Только после успешного завершения транзакции показываем экран "Your vote has been recorded"
        setSubmitted(true);
        setStep(2);
        setCountdown(55);
        
        // Save data to database
        try {
          const response = await fetch('/api/decrypted-throne', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          discord_name: discordName === 'anonymous' ? 'anonymous_' + wallet.substring(0, 8) : discordName,
          wallet_address: wallet,
              vote_option: votingOption, // Используем vote_option вместо voting_option
              arcium_tx_id: txSignature,
              lottery_ticket: ticket // Сохраняем лотерейный билет
        })
      });
      
          if (!response.ok) {
            const errorText = await response.text();
            setError(`API Error: ${response.status} - ${errorText}`);
          } else {
          }
          
        } catch (apiError) {
          setError(`API Error: ${apiError.message || 'Unknown error'}`);
          
      }
      } catch (err: any) {
        
        if (err.message && (
          err.message.includes("rejected") ||
          err.message.includes("cancelled") ||
          err.message.includes("User rejected")
        )) {
          setError("Transaction was cancelled by user");
        } else if (err.message && (
          err.message.includes("insufficient") ||
          err.message.includes("not enough") ||
          err.message.includes("balance") ||
          err.message.includes("funds") ||
          err.message.includes("SOL") ||
          err.message.includes("lamports") ||
          err.message.includes("fee") ||
          err.message.includes("cost") ||
          err.message.includes("payment") ||
          err.message.includes("account") ||
          err.message.includes("debit an account") ||
          err.message.includes("no record of a prior credit") ||
          err.message.toLowerCase().includes("insufficient") ||
          err.message.toLowerCase().includes("balance") ||
          err.message.toLowerCase().includes("funds")
        )) {
          setError("Insufficient balance. Please get some SOL from the faucet.");
        } else {
          // Упрощаем сообщение об ошибке - не показываем технические детали
          setError("Transaction error occurred");
        }
        setIsSubmitting(false);
      }
    } catch (err: any) {
      setError("Transaction error occurred");
      setIsSubmitting(false);
    }
  };
  
  const questions: Question[] = [
    {
      text: "What is privacy like?",
      options: ["A banana", "A fridge", "A diary", "rfvytdsghhtfcvvhhgssccvhyt"],
      correctAnswer: 3
    },
    {
      text: "Why is data split in Arcium?",
      options: ["To help Loosty make puzzles", "To increase security", "So everyone can get a piece", "To get laid"],
      correctAnswer: 1
    },
    {
      text: "What can we encrypt or compute with Arcium?",
      options: ["Something, but not everything", "Anything except something", "Only something", "Encrypt anything, compute everything"],
      correctAnswer: 3
    },
    {
      text: "What’s privacy’s biggest enemy?",
      options: ["Nosiness", "Parents", "Trump’s wife", "Saying “I’ve got nothing to hide”"],
      correctAnswer: 3
    },
    {
      text: "In Arcium, what does “Dark Pool” mean?",
      options: ["A pool where you can swim with your bare ass because it’s dark", "A hidden place for computation", "A resting zone for gangsters", "Just pick B, for god’s sake"],
      correctAnswer: 1
    }
  ];
  
  // Render option with correct/incorrect indicator when results are shown
  const renderOption = (qIndex: number, oIndex: number, option: string) => {
    const isSelected = answers[qIndex] === oIndex;
    const isCorrect = oIndex === questions[qIndex].correctAnswer;
    const showResult = showQuizResults;
    
    let borderClass = 'border-purple-500/50';
    let bgClass = 'bg-transparent';
    let hoverClass = '';

    if (showResult) {
      if (isSelected) {
        if (isCorrect) {
          borderClass = 'border-green-500';
          bgClass = 'bg-green-900/30';
        } else {
          borderClass = 'border-red-500';
          bgClass = 'bg-red-900/30';
        }
      } else if (isCorrect) {
        borderClass = 'border-green-500/70';
        bgClass = 'bg-green-900/10';
      }
    } else if (isSelected) {
      borderClass = 'border-purple-500';
      bgClass = 'bg-purple-900/50';
    } else {
      // Для невыбранных опций добавляем hover эффекты
      hoverClass = 'hover:bg-purple-900/20 hover:border-purple-500';
    }
    
    return (
      <div 
        key={oIndex}
        onClick={() => handleAnswerSelect(qIndex, oIndex)}
        className={`p-3 rounded-lg cursor-pointer transition-all border ${borderClass} ${bgClass} ${hoverClass} hover:border-opacity-80`}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <div className={`min-w-[16px] min-h-[16px] w-4 h-4 rounded-full mr-3 flex-shrink-0 ${
              isSelected ? 'bg-purple-500' : 'bg-purple-500/30'
            }`}>
              {isSelected && (
                <div className="w-full h-full flex items-center justify-center">
                  <div className="w-2 h-2 bg-white rounded-full"></div>
                </div>
              )}
            </div>
            <span className="text-white text-sm">{option}</span>
          </div>
          {showResult && (
            <div className="flex items-center">
              {isCorrect && (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-green-500" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              )}
              {isSelected && !isCorrect && (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-red-500" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              )}
            </div>
          )}
        </div>
        
      </div>
    );
  };
  
  if (!shouldRender) return null;
  
  // Обновляем проверку для отображения разных экранов
  // Экран "You've Already Participated!" показываем только при повторном открытии модального окна,
  // а не сразу после голосования
    if (showResults) {
    return (
      <div className="fixed inset-0 z-50">
        <style dangerouslySetInnerHTML={{
          __html: `
            .modal-content {
              opacity: 0;
              transform: scale(0.5);
              transition: all 0.15s ease-out;
            }

            .modal-content.entering {
              opacity: 1;
              transform: scale(1);
            }

            .modal-content.exiting {
              opacity: 0;
              transform: scale(0.5);
            }
          `
        }} />
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" onClick={handleClose}></div>
        <div className="fixed inset-0 flex items-center justify-center">
          <div className={`bg-black/80 rounded-xl shadow-xl border border-purple-800/50 p-6 max-w-xl w-full mx-4 animate-border-glow modal-content ${isAnimating ? 'entering' : 'exiting'}`}>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-white">Decrypted Throne Results</h2>
              <button
                onClick={handleClose}
                className="text-purple-400 transition-colors p-1"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="hover:text-white transition-colors duration-100 ease-out">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>
            
            <div className="space-y-6">
              <p className="text-yellow-400 mb-4 font-bold text-center">
                Voting is now closed. Final results are available.
              </p>
              
              <h3 className="text-lg font-semibold text-white">Final Results:</h3>
              
              {/* Визуализация результатов голосования */}
              <div className="space-y-4">
                                  {[0, 1, 2].map((optionIndex) => {
                  const isWinner = winningOption === optionIndex;
                  const percentage = getPercentage(optionIndex);
                  const voteCount = getVoteCount(optionIndex);
                  
                  return (
                    <div key={optionIndex} className="space-y-1">
                      <div className="flex justify-between text-sm">
                        <span className={`${isWinner ? 'font-bold text-purple-400' : 'text-gray-300'}`}>
                          Milian
                        </span>
                        <span className="text-gray-300">
                          {percentage}% ({voteCount} votes)
                        </span>
                      </div>
                      <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
                        <div 
                          className={`h-full ${isWinner ? 'bg-purple-500' : 'bg-gray-500'}`}
                          style={{ width: `${percentage || 1}%` }}
                        ></div>
                      </div>
                    </div>
                  );
                })}
              </div>
              
              {/* Информация об общем количестве голосов */}
              <p className="text-gray-400 text-sm mt-4">
                Total votes: {totalVotes}
              </p>
              
              {/* Информация о том, что голосование было анонимным */}
              <div className="mt-4 pt-4 border-t border-gray-700/50">
                <p className="text-gray-400 text-xs leading-relaxed">
                  This voting was conducted using Arcium's confidential computing capabilities to ensure truly anonymous voting on Solana. Individual votes remained private while still allowing for verifiable results. Arcium uses Multi-Party Computation (MPC) to achieve a trust-minimized setup for confidential voting.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  if (hasCompleted && !submitted) {
    return (
      <div className="fixed inset-0 z-50">
        <style dangerouslySetInnerHTML={{
          __html: `
            .modal-content {
              opacity: 0;
              transform: scale(0.5);
              transition: all 0.15s ease-out;
            }

            .modal-content.entering {
              opacity: 1;
              transform: scale(1);
            }

            .modal-content.exiting {
              opacity: 0;
              transform: scale(0.5);
            }
          `
        }} />
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" onClick={handleClose}></div>
        <div className="fixed inset-0 flex items-center justify-center">
          <div className={`bg-black/80 rounded-xl shadow-xl border border-purple-800/50 p-6 max-w-xl w-full mx-4 animate-border-glow modal-content ${isAnimating ? 'entering' : 'exiting'}`}>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-white">Decrypted Throne</h2>
              <button
                onClick={handleClose}
                className="text-purple-400 hover:text-white transition-colors duration-200 ease-out p-1 rounded"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>
            
            <div className="text-center py-5">
              <h3 className="text-xl font-bold text-purple-400 mb-3 fade-in">You've Already Participated!</h3>
              <p className="text-gray-300 mb-4 text-sm fade-in delay-100">
                You have already completed the Decrypted Throne event. Thank you for your participation!
              </p>
              
              {lotteryTicket && (
                <div className="bg-purple-900/30 border border-purple-700/50 rounded-lg p-4 my-6 fade-in delay-300">
                  <p className="text-purple-300 font-medium mb-3">Sacred Parasol has brought you a gift</p>
                  <p className="text-yellow-400 font-mono text-lg font-bold tracking-wider text-center mb-1 animate-pulse">
                    {lotteryTicket}
                  </p>
                  <p className="text-gray-400 text-xs text-center italic">the prize will be announced soon</p>
                </div>
              )}
              
              <button 
                onClick={() => {
                  // Создаем функцию для применения глитч-эффекта и сброса голоса
                  const handleResetVote = async () => {
                    try {
                      // Генерируем случайные линии для глитч-эффекта
                      const generateLines = () => {
                        const lines = [];
                        const lineCount = Math.floor(Math.random() * 15) + 8;
                        
                        for (let i = 0; i < lineCount; i++) {
                          const position = Math.random() * 100;
                          const colors = [
                            'rgba(255, 50, 50, 0.9)', 
                            'rgba(255, 100, 100, 0.8)',
                            'rgba(200, 0, 0, 0.7)',
                            'rgba(255, 0, 0, 0.8)'
                          ];
                          const color = colors[Math.floor(Math.random() * colors.length)];
                          lines.push({ position, color });
                        }
                        return lines;
                      };
                      
                      // Создаем сильный глитч-эффект
                      setGlitchedText(createGlitchText("Resetting Vote..."));
                      
                      // Серия интенсивных глитчей
                      for (let i = 0; i < 5; i++) {
                        setGlitchLines(generateLines());
                        await new Promise(resolve => setTimeout(resolve, 70));
                        setGlitchedText(createGlitchText("Deleting Data..."));
                      }
                      
                      // Удаляем пользователя из базы данных
                      const response = await fetch('/api/decrypted-throne/reset', {
                        method: 'POST',
                        headers: {
                          'Content-Type': 'application/json'
                        },
                        body: JSON.stringify({
                          wallet_address: wallet
                        })
                      });
                      
                      if (response.ok) {
                        // Полный сброс всех состояний
                        setGlitchedText("");
                        setGlitchLines([]);
                        setHasCompleted(false);
                        setSubmitted(false);
                        setStep(0);
                        setDiscordName('');
                        setAnswers([null, null, null, null, null]);
                        setVotingOption(null); // Сбрасываем выбранный вариант голосования
                        setShowQuizResults(false);
                        setError(null);
                        setArciumTxId(null);
                        setArciumStatus(null);
                        setLotteryTicket(null);
                        setCountdown(55);
                        setCurrentAnimationStep(-1);
                        setAnimationComplete(false);
                        setIsSubmitting(false); // Важно: сбрасываем флаг отправки
                        
                        // Принудительно выполняем полную перезагрузку компонента
                        handleClose(); // Закрываем модальное окно с полным сбросом состояний
                        
                        // Небольшая пауза перед повторным открытием
                        await new Promise(resolve => setTimeout(resolve, 500));
                      } else {
                        console.error('Failed to reset vote');
                      }
                    } catch (err) {
                      console.error('Error resetting vote:', err);
                    }
                  };
                  
                  // Вызываем функцию и обрабатываем ошибки
                  handleResetVote().catch(err => console.error('Failed to reset vote:', err));
                }}
                className="mt-3 py-2 px-4 font-medium w-full btn-textured-danger text-white transition-colors fade-in delay-800 text-sm border-2 border-red-500/80"
              >
                Reset Vote
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  // Показываем анимацию загрузки при проверке
  if (isLoading) {
    return (
      <div className="fixed inset-0 z-50">
        <style dangerouslySetInnerHTML={{
          __html: `
            .modal-content {
              opacity: 0;
              transform: scale(0.5);
              transition: all 0.15s ease-out;
            }

            .modal-content.entering {
              opacity: 1;
              transform: scale(1);
            }

            .modal-content.exiting {
              opacity: 0;
              transform: scale(0.5);
            }
          `
        }} />
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose}></div>
        <div className="fixed inset-0 flex items-center justify-center">
          <div className={`bg-black/80 rounded-xl shadow-xl border border-purple-800/50 p-6 max-w-xl w-full mx-4 animate-border-glow modal-content ${isAnimating ? 'entering' : 'exiting'}`}>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-white">Decrypted Throne</h2>
              {/* Не показываем кнопку закрытия при загрузке */}
            </div>
            
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-purple-500"></div>
              <span className="ml-3 text-gray-300">Checking your status...</span>
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="fixed inset-0 z-50">
      <style dangerouslySetInnerHTML={{
        __html: `
          .modal-content {
            opacity: 0;
            transform: scale(0.5);
            transition: all 0.15s ease-out;
          }

          .modal-content.entering {
            opacity: 1;
            transform: scale(1);
          }

          .modal-content.exiting {
            opacity: 0;
            transform: scale(0.5);
          }

          ${glitchStyles}
        `
      }} />
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose}></div>
      <div className="fixed inset-0 flex items-center justify-center">
        <div className={`bg-black/80 rounded-xl shadow-xl border border-purple-800/50 p-6 max-w-2xl w-full mx-4 animate-border-glow max-h-[90vh] flex flex-col modal-content ${isAnimating ? 'entering' : 'exiting'} ${glitchedText ? 'glitch-container' : ''}`}>
          <div className="flex justify-between items-center mb-6 flex-shrink-0">
            <h2 className="text-2xl font-bold text-white">Decrypted Throne</h2>
            {/* Убираем кнопку закрытия, если пользователь на этапе "спасибо" после голосования */}
            {!(step === 2 && submitted) && (
            <button
              onClick={handleClose}
              className="text-purple-400 transition-colors p-1 rounded-none"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="hover:text-white transition-colors duration-100 ease-out">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
            )}
          </div>
          
          <div className="overflow-y-auto flex-grow scrollbar-thin scrollbar-thumb-purple-600 scrollbar-track-transparent pr-1 -mr-1">
          {step === 0 && (
            <div className="mb-6">
              <div className="mb-6 text-center">
                <img src="/DecryptedThrone.png" alt="Decrypted Throne" className="max-h-48 mx-auto mb-4 rounded-lg" />
                  <h3 className="text-xl font-bold text-purple-400 mb-3">Welcome to Decrypted Throne</h3>
                  <div className="text-gray-300 space-y-3 text-sm">
                    <p className="font-medium text-base">
                      Three faces. One choice. You judge. Only one rises.
                    </p>
                    <p className="leading-relaxed">
                      This isn't just a poll, it's a throne game. A challenge to see who truly deserves to rise.
                      You're at the center of a court, casting a vote through 
                      a confidential system that favors the one who best decrypts themselves.
                    </p>
                    <p className="leading-relaxed">
<span className="bg-gradient-to-r from-purple-700 via-fuchsia-400 to-indigo-800 bg-clip-text text-transparent font-medium animate-gradient-x">After each vote, you'll receive a lottery ticket.</span> And while you're thinking, 
                      we'll drop a simple, clear truth. Here's the first:
                    </p>
                    <p className="text-gray-300 font-medium">
                      With Arcium, you can decrypt yourself, when needed. Just like the contenders in this throne room.
                    </p>
                    <p className="font-medium text-purple-400">
                      Ready? Step into the Throne Room.
                    </p>
                  </div>
              </div>
              
                <div className="mb-4">
                  <div className="flex justify-between items-center">
                    <label htmlFor="discord-name" className="block text-xs font-medium text-gray-300 mb-1">
                      Discord Username <span className="text-gray-500">(optional)</span>
                    </label>
                    <button
                      onClick={() => {
                        setDiscordName('anonymous');
                        setDiscordError(null);
                      }}
                      className="text-xs text-purple-400 hover:text-purple-300 px-2 py-1 transition-colors duration-100 ease-out"
                    >
                      Skip
                    </button>
                  </div>
                  <input
                    type="text"
                    id="discord-name"
                    value={discordName === 'anonymous' ? '' : discordName}
                    onChange={(e) => {
                      setDiscordName(e.target.value);
                      // При изменении имени сбрасываем ошибку
                      if (discordError) {
                        setDiscordError(null);
                      }
                    }}
                    placeholder="Enter your Discord username or skip"
                    className="w-full px-3 py-2 rounded-lg bg-black/30 border border-purple-800/50 text-white text-sm focus:outline-none focus:ring-2 focus:ring-purple-600"
                  />
                  {discordName === 'anonymous' && (
                    <p className="text-xs text-purple-400 mt-1">You'll participate anonymously</p>
                  )}
                </div>
              
                <button
                  onClick={handleStartQuiz}
                  disabled={(!discordName.trim() && discordName !== 'anonymous') || !publicKey || checkingDiscord}
                  className={`w-full py-2 px-4 border-2 border-purple-500/80 font-medium text-sm flex items-center justify-center
                    ${(discordName.trim() || discordName === 'anonymous') && publicKey && !checkingDiscord
                      ? 'btn-textured text-white'
                      : 'btn-textured-secondary text-white/50 cursor-not-allowed opacity-50'
                    } transition-colors`}
                >
                  {checkingDiscord ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-t-transparent border-white mr-2"></div>
                      Checking...
                    </>
                  ) : 'Start'}
                </button>
              
              {discordError && (
                <p className="mt-2 text-red-500 text-xs">
                  {discordError}
                </p>
              )}
              
              {!publicKey && (
                <p className="mt-2 text-yellow-500 text-xs">
                  Please connect your wallet to participate
                </p>
              )}
            </div>
          )}
          
          {step === 1 && (
            <div className="mb-6">
                <h3 className="text-xl font-bold text-purple-400 mb-3 text-center">Knowledge Quiz</h3>
              
              <div className="max-h-[60vh] overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-purple-600 scrollbar-track-transparent">
                  <div className="space-y-4">
                  {questions.map((question, qIndex) => (
                      <div key={qIndex} className="bg-black/30 p-3 rounded-lg border border-purple-800/30">
                        <p className="text-white mb-2 font-medium text-sm">
                        {qIndex + 1}. {question.text}
                      </p>
                      <div className="space-y-2">
                        {question.options.map((option, oIndex) => renderOption(qIndex, oIndex, option))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="mt-6">
                <button
                  onClick={handleQuizSubmit}
                  disabled={!allQuestionsAnswered()}
                    className={`w-full py-2 px-4 border-2 border-purple-500/80 font-medium text-sm
                    ${allQuestionsAnswered()
                      ? 'btn-textured text-white'
                      : 'btn-textured-secondary text-white/50 cursor-not-allowed opacity-50'
                    } transition-colors`}
                >
                  {showQuizResults ? 'Continue' : 'Check Answers'}
                </button>
              </div>
            </div>
          )}
          
          {step === 2 && !submitted && (
            <div className="mb-6">
                <h3 className="text-xl font-bold text-purple-400 mb-3 text-center">Final Vote</h3>
              
                <div className="bg-black/30 p-3 rounded-lg border border-purple-800/30 mb-4">
                  <p className="text-white mb-3 font-medium text-center text-sm">
                    Select the winner of the event
                </p>
                
                  <div className="space-y-2">
                  {[
                    "Milian",
                    "Milian",
                    "Milian"
                  ].map((option, index) => {
                    // Создаем уникальный идентификатор для каждой опции
                    const optionId = `voting-option-${index}`;
                    
                    return (
                      <div 
                        key={index}
                        id={optionId}
                                                  onClick={() => {
                            if (!isSubmitting) {
                              // Используем функцию выбора опции голосования
                              handleVotingOptionSelect(index);
                            }
                          }}
                        className={`p-3 rounded-lg transition-all border ${
                          votingOption === index
                            ? 'border-purple-500 bg-purple-900/50'
                            : 'border-purple-500/30 hover:border-purple-500 hover:bg-purple-900/20'
                        } ${isSubmitting ? 'cursor-not-allowed opacity-70' : 'cursor-pointer'}`}
                      >
                        <div className="flex items-center">
                          <div className={`min-w-[16px] min-h-[16px] w-4 h-4 rounded-full mr-3 flex-shrink-0 ${
                            votingOption === index
                              ? 'bg-purple-500'
                              : 'bg-purple-500/30'
                          }`}>
                            {votingOption === index && (
                              <div className="w-full h-full flex items-center justify-center">
                                <div className="w-2 h-2 bg-white rounded-full"></div>
                              </div>
                            )}
                          </div>
                          <span className="text-white text-sm">{option}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
              
              <button
                onClick={handleVoteSubmit}
                  disabled={votingOption === null || isSubmitting}
                  className={`w-full py-2 px-4 rounded-none border-2 border-purple-500/80 font-medium text-sm
                    ${votingOption !== null && !isSubmitting
                    ? 'btn-textured text-white'
                    : 'btn-textured-secondary text-white/50 cursor-not-allowed opacity-50'
                  } transition-colors`}
              >
                  {isSubmitting ? arciumStatus || 'Submitting...' : 'Submit Vote'}
              </button>
                
                {error && (
                  <p className="mt-3 text-red-500 text-xs">
                    {error}
                  </p>
                )}
            </div>
          )}
          
          {step === 2 && submitted && (
            <div className="mb-6 text-center">
                <div className="mb-6">
                  {!animationComplete ? (
                    // Анимированные шаги с эффектом печатающего текста
                    <div 
                      className="space-y-4 overflow-hidden w-full"
                      style={{
                        position: 'relative'
                      }}
                    >
                      {/* Глитч-эффект на весь контейнер */}
                      {glitchLines.map((line, index) => (
                        <div 
                          key={`container-line-${index}`} 
                          className="horizontal-glitch-line" 
                          style={{
                            top: `${line.position}%`,
                            backgroundColor: line.color,
                            height: `${Math.random() * 2 + 1}px`,
                            opacity: Math.random() * 0.6 + 0.4,
                            position: 'absolute',
                            left: 0,
                            width: '100%',
                            zIndex: 10
                          }}
                        />
                      ))}
                      <div 
                        key={`step-${currentAnimationStep}`}
                        className={`text-left bg-black/30 p-5 rounded-lg border border-purple-800/30 transition-all duration-700 ease-in-out w-full ${glitchedText ? 'glitch-container' : ''}`}
                        style={{
                          opacity: 1,
                          animation: currentAnimationStep > 0 ? `glitchEffect 700ms ease-out` : '',
                          wordBreak: 'break-word',
                          overflowWrap: 'break-word',
                          boxSizing: 'border-box',
                          position: 'relative'
                        }}
                      >
                        {/* Контейнер для текста с полным отступом */}
                        <div className="w-full mb-4 min-h-[6rem] relative">
                          {/* Невидимый элемент с полным текстом для правильного расчета размеров */}
                          {currentAnimationStep >= 0 && (
                            <div
                              className="text-purple-300 font-medium leading-relaxed px-4 break-words text-sm md:text-base font-sans w-full invisible absolute"
                              style={{
                                textAlign: 'left',
                                display: 'block',
                                visibility: 'hidden',
                                wordWrap: 'break-word',
                                wordBreak: 'break-word',
                                overflowWrap: 'break-word'
                              }}
                            >
                              {currentAnimationStep >= 0 ? animationSteps[currentAnimationStep].heading : ''}
                            </div>
                          )}
                          <div
                            className="text-purple-300 font-medium leading-relaxed px-4 break-words text-sm md:text-base font-sans w-full"
                                                          style={{
                                textAlign: 'left',
                                display: 'block',
                                position: 'relative',
                                visibility: 'visible',
                                wordWrap: 'break-word',
                                wordBreak: 'break-word',
                                overflowWrap: 'break-word',
                                minHeight: '3rem',
                                padding: '0.5rem 1rem'
                              }}
                            >
                                                           <div style={{ position: 'relative', width: '100%' }}>
                                                              <span 
                                style={{ display: 'inline', whiteSpace: 'normal' }}
                                className={glitchedText ? "glitch-text" : ""}
                                data-text={animationText} // Для псевдоэлементов ::before и ::after
                              >
                                {glitchedText || animationText || ' '}{/* Показываем глитч-версию или обычный текст */}
                                {isTyping && <span className="animate-pulse" style={{ display: 'inline', verticalAlign: 'baseline' }}>|</span>}
                              </span>
                            </div>
                            </div>
                        </div>
                        
                        {currentAnimationStep >= 0 && (
                          <div className="text-gray-400 text-xs md:text-sm space-y-3 font-mono max-h-[12rem] overflow-y-auto px-4 w-full">
                            {animationSteps[currentAnimationStep].details.map((detail, index) => {
                                // Извлекаем галочку из начала строки
                                const hasCheckmark = detail.startsWith("✅");
                                const detailText = hasCheckmark ? detail.substring(1).trim() : detail;
                                
                                return (
                                                                      <div 
                                    key={index} 
                                    className="flex items-start gap-2 border-l-2 border-purple-500/40 pl-2 py-1 w-full" 
                                    style={{ 
                                      opacity: 0, 
                                      transition: 'opacity 800ms ease-in, transform 800ms ease-out',
                                      transitionDelay: `${index * 400}ms`,
                                      transform: 'translateX(10px)',
                                    }}
                                    ref={el => {
                                      if (el) {
                                        setTimeout(() => {
                                          el.style.opacity = '1';
                                          el.style.transform = 'translateX(0)';
                                          // Добавляем эффект глитча вместо простой пульсации
                                          el.style.animation = `textGlitch 0.3s ease-out ${index * 100}ms`;
                                          // Добавляем класс анимации пульсации через небольшую задержку
                                          setTimeout(() => {
                                            if (el.classList) el.classList.add('animate-pulse-slow');
                                          }, 800);
                                        }, 100);
                                      }
                                    }}
                                  >
                                    <div className="flex-none" style={{ width: '24px', height: '24px' }}>
                                      {hasCheckmark && (
                                        <span 
                                          className="inline-block text-green-500 opacity-0"
                                          style={{
                                            transition: 'opacity 500ms ease-in, transform 500ms ease-out',
                                            transitionDelay: `${(index * 400) + 800}ms`,
                                          }}
                                          ref={el => {
                                            if (el) {
                                              setTimeout(() => {
                                                el.style.opacity = '1';
                                              }, 200);
                                            }
                                          }}
                                        >
                                          ✅
                                        </span>
                                      )}
                                    </div>
                                    <span className="flex-1 break-words whitespace-normal overflow-wrap-anywhere">{detailText}</span>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                      </div>
                    </div>
                  ) : (
                    // Финальный экран с лотерейным билетом
                    <div 
                      className="final-screen" 
                      style={{ 
                        transition: 'opacity 300ms ease-in'
                      }}
                    >
                      <div className="w-16 h-16 mx-auto bg-green-600/20 rounded-full flex items-center justify-center fade-in">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <h3 className="text-xl font-bold text-green-500 mt-3 mb-5 fade-in delay-300">Your vote has been recorded!</h3>
                      <p className="text-purple-300 font-medium mb-4 fade-in delay-500">Sacred Parasol has brought you a gift</p>
                      
                      <div className="bg-purple-900/30 border border-purple-700/50 rounded-lg p-4 my-4 fade-in delay-700">
                        <p className="text-yellow-400 font-mono text-lg font-bold tracking-wider text-center mb-1 animate-pulse">
                          {lotteryTicket}
                        </p>
                        <p className="text-gray-400 text-xs text-center italic">the prize will be announced soon</p>
                      </div>
                      
                      {arciumTxId && (
                        <div className="bg-purple-900/30 border border-purple-700/50 rounded-lg p-3 my-3 fade-in delay-900">
                          <div className="flex items-center gap-2">
                            <span className="text-white text-xs">Transaction:</span>
                            <a 
                              href={`https://explorer.solana.com/tx/${arciumTxId}?cluster=devnet`} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-blue-400 text-xs hover:text-blue-300 underline"
                              title={arciumTxId}
                            >
                              {arciumTxId.length > 12 
                                ? `${arciumTxId.substring(0, 8)}...${arciumTxId.substring(arciumTxId.length - 8)}`
                                : arciumTxId
                              }
                            </a>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
              </div>
              
              <button
                  onClick={handleClose}
                disabled={countdown > 0}
                  className={`w-full py-2 px-4 rounded-none border-2 border-purple-500/80 font-medium text-sm fade-in delay-900
                  ${countdown > 0
                    ? 'btn-textured-secondary text-white/50 cursor-not-allowed opacity-50'
                    : 'btn-textured text-white'
                  } transition-colors`}
              >
                  {countdown > 0 ? `Close (wait ${countdown}s)` : 'Close'}
              </button>
                
                {error && (
                  <p className="mt-3 text-yellow-500 text-xs">
                    Note: Your vote was recorded on the blockchain but there was an issue saving some data: {error}
                  </p>
                )}
            </div>
          )}
          
            <div className="mt-4 pt-3 border-t border-gray-700/50">
            <p className="text-gray-400 text-xs leading-relaxed">
              This voting system uses Arcium's confidential computing capabilities to ensure truly anonymous voting on Solana. Your individual votes remain private while still allowing for verifiable results. Arcium uses Multi-Party Computation (MPC) to achieve a trust-minimized setup for confidential voting, addressing a key challenge in prediction markets where transparent voting can lead to cascade effects that distort outcomes.
            </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}; 